import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import { getAllEntries, deleteIncome, deleteExpense, deleteSale, deletePurchaseOrder, deletePurchaseEntry, deleteUserTransfer, deleteRecurringEntry, bulkDeleteIncome, bulkDeleteExpense, bulkDeleteSales } from '../lib/api';
import { formatCurrency, formatDate, getToday, getDateRange, formatApiErrorDetail } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { Trash2, ExternalLink } from 'lucide-react';
import { ExportToolbar } from '../components/ui/export-toolbar';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'income', label: 'Income' },
  { value: 'expense', label: 'Expense' },
  { value: 'sales', label: 'Sales' },
  { value: 'purchase_orders', label: 'Purchase Orders' },
  { value: 'purchase_entries', label: 'Purchase Entries' },
  { value: 'user_transfer', label: 'Transfers' },
  { value: 'recurring', label: 'Recurring' },
];

const TYPE_COLORS = {
  income: 'default',
  expense: 'destructive',
  sales: 'secondary',
  purchase_orders: 'default',
  purchase_entries: 'destructive',
  user_transfer: 'outline',
  recurring: 'secondary',
};

const TYPE_LABELS = {
  income: 'Income',
  expense: 'Expense',
  sales: 'Sale',
  purchase_orders: 'PO',
  purchase_entries: 'PE',
  user_transfer: 'Transfer',
  recurring: 'Recurring',
};

const QUICK_BUTTONS = [
  { key: 'today', label: 'Today' },
  { key: 'this-week', label: 'This Week' },
  { key: 'current-month', label: 'This Month' },
  { key: 'previous-month', label: 'Last Month' },
  { key: 'current-quarter', label: 'This Qtr' },
  { key: 'previous-quarter', label: 'Last Qtr' },
  { key: 'current-fy', label: 'This FY' },
  { key: 'previous-fy', label: 'Last FY' },
  { key: 'full-ledger', label: 'Full Ledger' },
];

const MODULE_ROUTES = {
  income: '/income',
  expense: '/expense',
  sales: '/sales',
  purchase_orders: '/purchase',
  purchase_entries: '/purchase',
  user_transfer: '/ledger',
  recurring: '/recurring',
};

const AllEntries = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const currentMonth = getDateRange('current-month');
  const [dateRange, setDateRange] = useState({ start_date: currentMonth.start_date, end_date: currentMonth.end_date });
  const [activeQuick, setActiveQuick] = useState('current-month');
  const [customStart, setCustomStart] = useState(currentMonth.start_date);
  const [customEnd, setCustomEnd] = useState(currentMonth.end_date);

  const { sortedData, sortKey, sortDirection, handleSort } = useSortableTable(entries, 'date', 'desc');

  const fetchData = useCallback(async (startDate, endDate, type) => {
    setLoading(true);
    try {
      const res = await getAllEntries(startDate, endDate, type);
      setEntries(res.data);
      setSelectedIds([]);
    } catch (err) { toast.error('Failed to fetch entries'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(dateRange.start_date, dateRange.end_date, filterType); }, [fetchData, dateRange.start_date, dateRange.end_date, filterType]);

  const handleQuick = (key) => {
    const range = getDateRange(key);
    setDateRange(range);
    setCustomStart(range.start_date);
    setCustomEnd(range.end_date);
    setActiveQuick(key);
    fetchData(range.start_date, range.end_date, filterType);
  };

  const handleCustomApply = () => {
    setDateRange({ start_date: customStart, end_date: customEnd });
    setActiveQuick('');
    fetchData(customStart, customEnd, filterType);
  };

  const handleTypeChange = (val) => {
    setFilterType(val);
    fetchData(dateRange.start_date, dateRange.end_date, val);
  };

  const handleDateQuick = useCallback((key) => {
    const range = getDateRange(key);
    setDateRange(range);
    setCustomStart(range.start_date);
    setCustomEnd(range.end_date);
    setActiveQuick(key);
    fetchData(range.start_date, range.end_date, filterType);
  }, [filterType, fetchData]);

  const handleDateCustom = useCallback(() => {
    setActiveQuick('');
    const startInput = document.querySelector('[data-testid="all-entries-start"]');
    if (startInput) startInput.focus();
  }, []);

  usePageCallbacks({ onDateQuick: handleDateQuick, onDateCustom: handleDateCustom });

  const handleDelete = async (entry) => {
    const label = TYPE_LABELS[entry.entry_type] || entry.entry_type;
    if (!window.confirm(`Delete this ${label} entry?`)) return;
    try {
      switch (entry.entry_type) {
        case 'income': await deleteIncome(entry.id); break;
        case 'expense': await deleteExpense(entry.id); break;
        case 'sales': await deleteSale(entry.id); break;
        case 'purchase_orders': await deletePurchaseOrder(entry.id); break;
        case 'purchase_entries': await deletePurchaseEntry(entry.id); break;
        case 'user_transfer': await deleteUserTransfer(entry.id); break;
        case 'recurring': await deleteRecurringEntry(entry.id); break;
        default: toast.error('Unknown entry type'); return;
      }
      toast.success(`${label} deleted`);
      fetchData(dateRange.start_date, dateRange.end_date, filterType);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} selected entries?`)) return;
    const selected = entries.filter(e => selectedIds.includes(`${e.entry_type}-${e.id}`));
    let deleted = 0;
    let failed = 0;
    for (const entry of selected) {
      try {
        switch (entry.entry_type) {
          case 'income': await deleteIncome(entry.id); break;
          case 'expense': await deleteExpense(entry.id); break;
          case 'sales': await deleteSale(entry.id); break;
          case 'purchase_orders': await deletePurchaseOrder(entry.id); break;
          case 'purchase_entries': await deletePurchaseEntry(entry.id); break;
          case 'user_transfer': await deleteUserTransfer(entry.id); break;
          case 'recurring': await deleteRecurringEntry(entry.id); break;
          default: failed++; continue;
        }
        deleted++;
      } catch (err) { console.error('Bulk delete entry failed:', err); failed++; }
    }
    toast.success(`Deleted ${deleted} entries${failed ? `, ${failed} failed` : ''}`);
    fetchData(dateRange.start_date, dateRange.end_date, filterType);
  };

  const handleGoToModule = (entry) => {
    const route = MODULE_ROUTES[entry.entry_type];
    if (route) navigate(route);
  };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  const toggleSelect = (key) => setSelectedIds(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const allSelected = sortedData.length > 0 && selectedIds.length === sortedData.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(sortedData.map(e => `${e.entry_type}-${e.id}`));
  };

  const totalAmount = entries.reduce((sum, e) => {
    if (e.entry_type === 'income' || e.entry_type === 'sales') return sum + (e.amount || 0);
    if (e.entry_type === 'expense' || e.entry_type === 'purchase_orders' || e.entry_type === 'purchase_entries') return sum - (e.amount || 0);
    return sum;
  }, 0);

  return (
    <div className="space-y-4" data-testid="all-entries-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">All Entries</h1>
          <p className="text-muted-foreground text-sm">Unified view of all transactions</p>
        </div>
        <ExportToolbar endpoint="/exports/all-entries" fileBaseName="all-entries" params={{ start_date: dateRange.start_date, end_date: dateRange.end_date, type: filterType || undefined }} testId="export-all-entries" />
      </div>

      {/* Quick Date Buttons */}
      <div className="flex sm:flex-wrap items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 scrollbar-hide">
        {QUICK_BUTTONS.map(btn => (
          <Button key={btn.key} type="button" variant={activeQuick === btn.key ? 'default' : 'outline'} size="sm" className="text-xs h-7 px-2 shrink-0" onClick={() => handleQuick(btn.key)} data-testid={`quick-${btn.key}`}>
            {btn.label}
          </Button>
        ))}
        <DateInput value={customStart} onChange={(e) => { setCustomStart(e.target.value); setActiveQuick(''); }} className={`h-7 w-[110px] sm:w-[130px] text-xs shrink-0 ${!activeQuick ? 'ring-2 ring-primary/60 ring-offset-1' : ''}`} data-testid="all-entries-start" />
        <span className="text-xs text-muted-foreground shrink-0">to</span>
        <DateInput value={customEnd} onChange={(e) => { setCustomEnd(e.target.value); setActiveQuick(''); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomApply(); } }} className={`h-7 w-[110px] sm:w-[130px] text-xs shrink-0 ${!activeQuick ? 'ring-2 ring-primary/60 ring-offset-1' : ''}`} data-testid="all-entries-end" />
        <Button type="button" size="sm" className="h-7 text-xs shrink-0" onClick={handleCustomApply} data-testid="all-entries-apply">Go</Button>
      </div>

      {/* Type Filter + Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="w-48">
          <SearchableSelect options={TYPE_OPTIONS} value={filterType} onChange={handleTypeChange} placeholder="Filter by type" data-testid="all-entries-type-filter" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-card shrink-0">
            <span className="text-sm text-muted-foreground">Count:</span>
            <span className="text-lg font-bold font-mono" data-testid="all-entries-count">{entries.length}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-card shrink-0">
            <span className="text-sm text-muted-foreground">Net:</span>
            <span className={`text-lg font-bold font-mono ${totalAmount >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="all-entries-net">
              {formatCurrency(Math.abs(totalAmount))}
              {totalAmount >= 0 ? ' Cr' : ' Dr'}
            </span>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && isAdmin && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="all-entries-bulk-bar">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}>Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="all-entries-bulk-delete"><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="w-10 cursor-pointer" onClick={toggleAll}><Checkbox checked={allSelected} className="pointer-events-none" data-testid="select-all-entries" /></TableHead>}
                  <TableHead className="w-16 sm:w-20"><SortableHeader label="Type" sortKey="source_module" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead><SortableHeader label="Date" sortKey="date" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="Category / Party" sortKey="category" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right"><SortableHeader label="Amount" sortKey="amount" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} className="justify-end" /></TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="Remarks" sortKey="remarks" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="hidden sm:table-cell"><SortableHeader label="By" sortKey="user_name" currentSortKey={sortKey} sortDirection={sortDirection} onSort={handleSort} /></TableHead>
                  <TableHead className="text-right w-[60px] sm:w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={isAdmin ? 8 : 7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                  ))
                ) : sortedData.length === 0 ? (
                  <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">No entries found for this period</TableCell></TableRow>
                ) : sortedData.map(entry => {
                  const key = `${entry.entry_type}-${entry.id}`;
                  return (
                  <React.Fragment key={key}>
                    <TableRow
                      className={`cursor-pointer sm:cursor-default ${expandedId === entry.id ? 'bg-muted/30' : ''} ${selectedIds.includes(key) ? 'bg-primary/5' : ''}`}
                      onClick={() => toggleExpand(entry.id)}
                      data-testid={`entry-row-${entry.id}`}
                    >
                      {isAdmin && <TableCell className="w-10" onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedIds.includes(key)} onCheckedChange={() => toggleSelect(key)} /></TableCell>}
                      <TableCell>
                        <Badge variant={TYPE_COLORS[entry.entry_type] || 'secondary'} className="text-[10px] px-1.5">
                          {TYPE_LABELS[entry.entry_type] || entry.entry_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-mono whitespace-nowrap">{entry.date ? formatDate(entry.date.substring(0, 10)) : '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell font-medium max-w-[200px] truncate">{entry.category || '-'}</TableCell>
                      <TableCell className={`text-right font-mono whitespace-nowrap ${entry.entry_type === 'income' || entry.entry_type === 'sales' ? 'text-emerald-600' : entry.entry_type === 'expense' || entry.entry_type === 'purchase_orders' || entry.entry_type === 'purchase_entries' ? 'text-red-600' : ''}`}>
                        {entry.amount > 0 ? formatCurrency(entry.amount) : '-'}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell max-w-[200px] truncate text-sm text-muted-foreground">{entry.remarks || '-'}</TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">{entry.user_name}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-0.5">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleGoToModule(entry); }} data-testid={`goto-${entry.id}`} title="Go to module">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                          {(isAdmin || entry.entry_type === 'income' || entry.entry_type === 'expense') && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(entry); }} data-testid={`delete-${entry.id}`} title="Delete">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Mobile expanded detail */}
                    {expandedId === entry.id && (
                      <TableRow className="sm:hidden bg-muted/20">
                        <TableCell colSpan={isAdmin ? 8 : 7} className="py-2 px-4 space-y-1 text-sm">
                          <div><span className="text-muted-foreground">Category:</span> <span className="font-medium">{entry.category || '-'}</span></div>
                          <div><span className="text-muted-foreground">Remarks:</span> {entry.remarks || '-'}</div>
                          <div><span className="text-muted-foreground">By:</span> {entry.user_name}</div>
                          {entry.extra?.status && <div><span className="text-muted-foreground">Status:</span> {entry.extra.status}</div>}
                          {entry.extra?.po_number && <div><span className="text-muted-foreground">PO#:</span> {entry.extra.po_number}</div>}
                          {entry.extra?.vehicle_number && <div><span className="text-muted-foreground">Vehicle:</span> {entry.extra.vehicle_number}</div>}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AllEntries;
